import { Box, Group, ScrollArea, Text } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import React, { useEffect, useMemo, useRef } from "react";
import { useAppSelector } from "../../../../../../hooks";
import { selectCurrentOpenIssueBoardInModal, selectCurrentOpenIssueInModal } from "../../../issueSelectors";

const IssueWorkflowProgress: React.FC = () => {
  const issue = useAppSelector(selectCurrentOpenIssueInModal);
  const issueBoard = useAppSelector(selectCurrentOpenIssueBoardInModal);
  const activeStepRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const workflowBuckets = useMemo(() => {
    return [...(issueBoard?.buckets ?? [])]
      .filter((bucket) => bucket.value != null && bucket.value !== "")
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [issueBoard?.buckets]);

  const currentBucket = issue?.bucket ?? "";

  useEffect(() => {
    if (issue == null || issueBoard?.useBucketsAsWorkflow !== true || workflowBuckets.length === 0) return;

    const activeStep = activeStepRef.current;
    const viewport = viewportRef.current;

    if (activeStep == null || viewport == null) return;

    const nextScrollLeft = activeStep.offsetLeft - viewport.clientWidth / 2 + activeStep.clientWidth / 2;
    viewport.scrollTo({ left: Math.max(nextScrollLeft, 0), behavior: "smooth" });
  }, [currentBucket, issue, issueBoard?.useBucketsAsWorkflow, workflowBuckets]);

  if (issue == null || issueBoard?.useBucketsAsWorkflow !== true || workflowBuckets.length === 0) {
    return null;
  }

  return (
    <Group
      gap="sm"
      wrap="nowrap"
      align="center"
      aria-label={`Arbeidsflyt: ${currentBucket}`}
      style={{
        minHeight: 34,
        overflow: "hidden",
        marginBottom: 4,
      }}
    >
      <ScrollArea type="never" viewportRef={viewportRef} style={{ minWidth: 0, flex: 1 }}>
        <Group gap={4} wrap="nowrap">
          {workflowBuckets.map((bucket, index) => {
            const isActive = bucket.value === currentBucket;

            return (
              <React.Fragment key={`${bucket.value}-${index}`}>
                <Box
                  ref={isActive ? activeStepRef : undefined}
                  px="sm"
                  py={4}
                  style={{
                    flex: "0 0 auto",
                    minWidth: 76,
                    maxWidth: isActive ? 220 : 160,
                    borderRadius: 4,
                    border: isActive ? "1px solid #6d98b8" : "1px solid transparent",
                    background: isActive ? "#eaf3f8" : "transparent",
                    color: isActive ? "#244f68" : "var(--mantine-color-gray-7)",
                  }}
                >
                  <Text size="sm" fw={isActive ? 700 : 500} truncate ta="center" lh={1.25}>
                    {bucket.value}
                  </Text>
                </Box>
                {index < workflowBuckets.length - 1 && (
                  <IconChevronRight
                    size={18}
                    stroke={2.4}
                    color={isActive ? "#6d98b8" : "var(--mantine-color-gray-5)"}
                    style={{ flex: "0 0 auto" }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Group>
      </ScrollArea>
    </Group>
  );
};

export default IssueWorkflowProgress;
